import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid } from '@mui/material';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { Text } from '@visx/text';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { localPoint } from '@visx/event';
import { max } from 'd3-array';
import PropTypes from 'prop-types';

import { getParams, thousandFormat } from '../../../../Utils/Functions';
import { daysRanges, activeLabelColor } from '../../../../Assets/js/constant';

const xSelector = (d) => d.daysRange;
const ySelector = (d) => d.outstandingAmount;
const colorSelector = (d) => d.color;

const AgeingChart = ({
  detailData,
  selectedYears,
  selectedDaysRanges,
  handleFilter,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [data, setData] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [resize, setResize] = useState(false);

  const isMountedRef = useRef(true);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const prepareData = useCallback(() => {
    let chartData = {
      Outstanding30Days: 0,
      Outstanding60Days: 0,
      Outstanding90Days: 0,
      Outstanding120Days: 0,
      Outstanding120PlusDays: 0,
      Total: 0,
    };

    detailData.forEach((d) => {
      chartData['Outstanding30Days'] += parseFloat(d.Outstanding30Days);
      chartData['Outstanding60Days'] += parseFloat(d.Outstanding60Days);
      chartData['Outstanding90Days'] += parseFloat(d.Outstanding90Days);
      chartData['Outstanding120Days'] += parseFloat(d.Outstanding120Days);
      chartData['Outstanding120PlusDays'] += parseFloat(
        d.Outstanding120PlusDays,
      );
      chartData['Total'] += parseFloat(d.OutstandingAmount);
    });

    if (detailData.length === 0) {
      setData([]);
      return;
    }

    let dataArray = [];
    let total = Math.round(chartData['Total']);
    let integral = 0;

    Object.keys(daysRanges).forEach((key) => {
      const item = daysRanges[key];
      const val = Math.round(chartData[item.daysRange]);

      if (item.daysRange === 'Total') {
        dataArray.push({
          ...item,
          outstandingAmount: total,
          integral: 0,
          percentage: 100,
        });
      } else {
        dataArray.push({
          ...item,
          outstandingAmount: val,
          integral: integral,
          percentage: Math.round((val / total) * 100),
        });
      }

      integral += val;
    });

    setData(dataArray);
  }, [detailData]);

  const getStrokeWidth = useCallback((key) => {
    const daysRange = xSelector(daysRanges[key]);
    return selectedDaysRanges.indexOf(daysRange) > -1
      ? 1.4
      : hoverIndex === key
      ? 0.8
      : 0;
  }, [selectedDaysRanges, hoverIndex]);

  const handleSelect = useCallback((event, key) => {
    const daysRange = xSelector(daysRanges[key]);

    let _selectedDaysRanges = selectedDaysRanges.slice();
    const index = selectedDaysRanges.indexOf(daysRange);

    if (event.shiftKey) {
      if (index > -1) {
        _selectedDaysRanges.splice(index, 1);
      } else {
        _selectedDaysRanges.push(daysRange);

        let tempArr = [];
        Object.keys(daysRanges).forEach((key) => {
          if (_selectedDaysRanges.indexOf(daysRanges[key].daysRange) > -1)
            tempArr.push(daysRanges[key].daysRange);
        });
        _selectedDaysRanges = tempArr;
      }
    } else {
      if (selectedDaysRanges.length === 1 && index > -1) {
        _selectedDaysRanges = [];
      } else {
        _selectedDaysRanges = [daysRange];
      }
    }

    handleFilter({ selectedDaysRanges: _selectedDaysRanges });
  }, [selectedDaysRanges, handleFilter]);

  const showTooltipHandler = useCallback((event, dataItem) => {
    const { x, y } = localPoint(event);

    showTooltip({
      tooltipData: dataItem,
      tooltipTop: y + 250,
      tooltipLeft: x + 30,
    });

    setHoverIndex(dataItem.key);
  }, [showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    setHoverIndex(null);
    hideTooltip();
  }, [hideTooltip]);

  const deSelectAll = useCallback(() => {
    handleFilter({ selectedDaysRanges: [] });
  }, [handleFilter]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize, prepareData]);

  useEffect(() => {
    if (isMountedRef.current) {
      deSelectAll();
      prepareData();
    }
  }, [detailData.length, selectedYears.length, deSelectAll, prepareData]);

  // Render logic
  const dataMax = max(data, ySelector);
  let xTransform = 0;
  let maxYValue = Math.round(dataMax * 1.1);

  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 7.5 * (thousandFormat(maxYValue).length + 1) + 15;
    else xTransform = 7.5 * thousandFormat(maxYValue).length + 15;
  }

  const width = (window.innerWidth - 16) / 2;
  const height = window.innerHeight / 6 + 7;
  const margin = {
    top: 20,
    right: 30,
    bottom: 20,
    left: xTransform,
  };
  const { xMax, yMax } = getParams(window, width, height, margin);

  // scales
  const xScale = scaleLinear({
    domain: [0, 5],
    range: [xMax / 12, xMax - width / 12],
  });
  const xAxisScale = scaleLinear({
    domain: [0, 6],
    range: [0, xMax],
  });

  const yScale = scaleLinear({
    domain: [0, Math.round(dataMax * 1.1)],
    range: [yMax, 0],
    nice: true,
  });

  let tooltipTimeout;

  return (
    <>
      <div className="subtitle mb-10">Ageing</div>

      <Grid container>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          <Group transform={`translate(${xTransform}, 20)`}>
            <AxisLeft
              scale={yScale}
              numTicks={3}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
              })}
              tickFormat={(value, index) => `$${thousandFormat(value)}`}
              tickStroke="black"
              stroke="black"
            />

            {data.map((d, i) => {
              const barHeight = yMax - yScale(ySelector(d));
              const offsetY = yMax - yScale(d.integral + ySelector(d));

              return (
                <Group key={`bar-${xSelector(d)}`}>
                  <Bar
                    width={xMax / 6 - xMax / 30}
                    height={isNaN(barHeight) ? 0 : barHeight}
                    x={xAxisScale(d.key) + xMax / 60}
                    y={isNaN(yMax - offsetY) ? 0 : yMax - offsetY}
                    fill={colorSelector(d)}
                    stroke="black"
                    strokeWidth={getStrokeWidth(d.key)}
                    data={{ x: xSelector(d), y: ySelector(d) }}
                    onClick={(event) => {
                      handleSelect(event, d.key);
                    }}
                    onMouseMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, d);
                    }}
                    onMouseLeave={(event) => {
                      tooltipTimeout = setTimeout(() => {
                        hideTooltipHandler();
                      }, 300);
                    }}
                    onTouchMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, d);
                    }}
                    onTouchEnd={(event) => {
                      tooltipTimeout = setTimeout(() => {
                        hideTooltipHandler();
                      }, 300);
                    }}
                  />
                  <Text
                    x={xScale(d.key) ? xScale(d.key) : 0}
                    y={yMax - offsetY ? yMax - offsetY : 0}
                    verticalAnchor="end"
                    textAnchor="start"
                    fontSize="11px"
                    dx={-5}
                    dy={-5}
                  >
                    {`${d.percentage}%`}
                  </Text>
                </Group>
              );
            })}

            {[0, 1, 2, 3, 4, 5].map((x, index) => {
              return (
                <rect
                  key={index}
                  x={(index * xMax) / 6}
                  y={yMax}
                  width={xMax / 6}
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) => handleSelect(event, index)}
                />
              );
            })}

            <AxisBottom
              top={yMax}
              left={0}
              scale={xAxisScale}
              hideZero
              numTicks={6}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                textAnchor: 'middle',
                fontSize: 11,
                fontFamily: 'Arial',
                dx: `${-xMax / 12}`,
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedDaysRanges.indexOf(
                        xSelector(daysRanges[formattedValue - 1]),
                      ) > -1
                        ? 'bold'
                        : 500,
                  }}
                  fill={
                    selectedDaysRanges.indexOf(
                      xSelector(daysRanges[formattedValue - 1]),
                    ) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleSelect(event, formattedValue - 1)
                  }
                >
                  {daysRanges[formattedValue - 1].text}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop}
            left={tooltipLeft}
          >
            <div className="bkg-white color-black">
              <p className="tooltipPanel" style={{ fontWeight: 'bold' }}>{`${
                tooltipData.text === 'Total' ? 'All' : tooltipData.text
              } days`}</p>
              <p className="tooltipPanel">{`$${thousandFormat(
                ySelector(tooltipData),
              )}`}</p>
              <p className="tooltipPanel">{`${tooltipData.percentage}% of total debt`}</p>
            </div>
          </TooltipWithBounds>
        )}
      </Grid>
    </>
  );
};

AgeingChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedDaysRanges: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default AgeingChart;
